/**
 * ReadOnlyTakeOverModal — the center "article in use" prompt.
 *
 * Shown when this session opens (or is demoted into) read-only because another
 * session — a teammate or the Hermes agent — currently holds the single-writer
 * lock. It both NOTIFIES (persistent, attention-grabbing, survives an inactive tab)
 * and ASKS before taking over, so a new tab never silently steals an active lock.
 *
 * The take-over goes through the backend-enforced, fail-closed `requestTakeOver`
 * (reload HEAD + version bump); this component only renders the choice.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React from "react";

export interface ReadOnlyTakeOverModalProps {
  open: boolean;
  holderName: string;
  holderKind: "human" | "agent";
  onTakeOver: () => void;
  onViewReadOnly: () => void;
}

export const ReadOnlyTakeOverModal: React.FC<ReadOnlyTakeOverModalProps> = ({
  open,
  holderName,
  holderKind,
  onTakeOver,
  onViewReadOnly,
}) => {
  const isAgent = holderKind === "agent";
  const title = isAgent ? "🤖 Article under review" : "🔒 Article in use";
  const body = isAgent
    ? `${holderName || "Hermes"} is currently reviewing this article. You're in read-only mode.`
    : `${holderName || "Someone"} is currently editing this article. You're in read-only mode.`;

  return (
    <Dialog
      open={open}
      onClose={onViewReadOnly}
      maxWidth="xs"
      fullWidth
      aria-labelledby="readonly-takeover-title"
    >
      <DialogTitle id="readonly-takeover-title" sx={{ fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
          {body}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.secondary", mt: 1 }}>
          Taking over will switch {isAgent ? "the agent" : "them"} to read-only and
          load the latest saved version before you start editing.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onViewReadOnly} variant="text" sx={{ textTransform: "none" }}>
          View read-only
        </Button>
        <Button
          onClick={onTakeOver}
          variant="contained"
          color="error"
          sx={{ textTransform: "none" }}
        >
          Take over{isAgent ? " from 🤖" : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReadOnlyTakeOverModal;
